import type { ReactElement, ReactNode } from 'react';
import type { Proposal } from '@shared/types';
import {
  deliveryKind,
  pricedLine,
  resolvedMomentumStage,
  visibleSections,
  type MomentumJourneyStage,
} from '../../lib/plan';
import { LINEWORK_DARK, LINEWORK_LIGHT } from './assets';

/**
 * Momentum_v1's shared visual vocabulary (design.md V6 "Momentum family —
 * tokens" + per-archetype spec): numbered eyebrows, pill/chip labels, the
 * dark contour-line motif, rounded numbered cards, dark stat/kernel cards,
 * bound metric tiles, and the dark-page footer meta row, styled in
 * styles.css under the (now-removed) `.tpl-momentum` scope. Moved here
 * unchanged when momentum_v1/v2 were sunset from the web app, because
 * momentum_v3 still reuses three family-agnostic facts about the document
 * from it: `MOMENTUM_STAGES`, `pageIndexLabel`, and (via JourneyPages.tsx)
 * `tacticsByStage`. Everything else this file exports (the decorative
 * components/tokens above) has no momentum_v3 consumer any more - momentum_v3
 * has its own decorative layer in `./primitives.tsx` - and is kept only
 * because this was a move, not a rewrite.
 */

/** Static, deck-wide breadcrumb for the dark-page footer meta row. */
export const FOOTER_BREADCRUMB = 'Strategy → Activation → Learning';

/**
 * The page's 1-based position among *visible* sections, zero-padded to two
 * digits ("04") - the same ordering the grid caption already numbers by.
 * Purely structural (derived from real document order), never fabricated.
 * Null when the section is hidden (excluded from the visible sequence).
 */
export function pageIndexLabel(proposal: Proposal, sectionId: string): string | null {
  const sections = visibleSections(proposal);
  const index = sections.findIndex((section) => section.id === sectionId);
  return index === -1 ? null : String(index + 1).padStart(2, '0');
}

/** The family's five funnel stages, display-labeled, in order (design.md V6 customer_journey). */
export const MOMENTUM_STAGES: { key: MomentumJourneyStage; label: string }[] = [
  { key: 'awareness', label: 'Awareness' },
  { key: 'consideration', label: 'Consideration' },
  { key: 'intent', label: 'Intent' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'retention', label: 'Retention' },
];

/**
 * Which momentum funnel stage each selected channel badges onto, and the
 * display labels for the channels in that stage - the one computation shared
 * by customer_journey (tactics per card), audience_profiles ("best use"
 * pills) and product_divider (the lime journey-stage chip), so every page
 * agrees on the same mapping.
 *
 * K3e: stage comes from `resolvedMomentumStage`, which favors the tactic's
 * own authored `funnelStage` over the fixed productId/family table - so the
 * journey slide agrees with the strategy the agent actually wrote instead
 * of silently overriding it whenever a channel's stage is unconventional
 * for this advertiser (e.g. paid_search used for top-of-funnel awareness).
 */
export function tacticsByStage(
  proposal: Proposal,
  labelFor: (productId: string) => string,
): Map<MomentumJourneyStage, string[]> {
  const map = new Map<MomentumJourneyStage, string[]>();
  for (const channel of proposal.mediaPlan.channels) {
    const priced = pricedLine(proposal, channel.productId);
    const kind = deliveryKind(priced);
    const stage = resolvedMomentumStage(proposal, channel.productId, kind);
    const label = labelFor(channel.productId);
    const list = map.get(stage) ?? [];
    list.push(label);
    map.set(stage, list);
  }
  return map;
}

/** Tiny uppercase tracked eyebrow: mint number (optional) + "/" + gray label. */
export function Eyebrow({
  number,
  label,
  inverse,
  className,
}: {
  number?: string | null;
  label: string;
  /** On a dark page ground - lightens the label tone. */
  inverse?: boolean;
  className?: string;
}): ReactElement {
  return (
    <div className={`mtm-eyebrow${inverse ? ' mtm-eyebrow-inverse' : ''}${className ? ` ${className}` : ''}`}>
      {number ? (
        <>
          <span className="mtm-eyebrow-num">{number}</span>
          <span className="mtm-eyebrow-sep" aria-hidden="true">
            /
          </span>
        </>
      ) : null}
      <span className="mtm-eyebrow-label">{label}</span>
    </div>
  );
}

/** Right-aligned tiny gray annotation near a headline (design.md tokens). */
export function Annotation({ children }: { children: ReactNode }): ReactElement {
  return <p className="mtm-annotation">{children}</p>;
}

export type PillTone = 'mint' | 'lime' | 'pale' | 'dark' | 'outline';

/** Rounded pill/chip label - the family's one shape for a short tag. */
export function Pill({
  tone = 'mint',
  children,
  className,
}: {
  tone?: PillTone;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return <span className={`mtm-pill mtm-pill-${tone}${className ? ` ${className}` : ''}`}>{children}</span>;
}

/** A numbered white rounded card - overview's four fields, audience profiles. */
export function NumberedCard({
  number,
  label,
  children,
  className,
}: {
  number: string;
  label?: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div className={`mtm-card${className ? ` ${className}` : ''}`}>
      <div className="mtm-card-head">
        <span className="mtm-card-num">{number}</span>
        {label ? <span className="mtm-card-label">{label}</span> : null}
      </div>
      <div className="mtm-card-body">{children}</div>
    </div>
  );
}

/** The dark ink/dark-card rounded panel - strategy kernel, stat panel, next step. */
export function DarkCard({ className, children }: { className?: string; children: ReactNode }): ReactElement {
  return <div className={`mtm-dark-card${className ? ` ${className}` : ''}`}>{children}</div>;
}

/** One bound metric tile; renders nothing when the value is absent (never fabricated). */
export function MetricTile({
  label,
  value,
  tag,
}: {
  label: string;
  value: ReactNode | null;
  tag?: ReactNode;
}): ReactElement | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="mtm-tile">
      <span className="mtm-tile-label">{label}</span>
      <span className="mtm-tile-value">
        {value}
        {tag}
      </span>
    </div>
  );
}

export function MetricTileRow({ children }: { children: ReactNode }): ReactElement {
  return <div className="mtm-tile-row">{children}</div>;
}

/**
 * The dark-page footer meta row: site + breadcrumb. Renders nothing when
 * neither is available rather than showing an empty row.
 */
export function FooterMeta({ website }: { website?: string | null }): ReactElement | null {
  if (!website) return null;
  return (
    <div className="mtm-footer-meta">
      <span className="mtm-footer-site">{website}</span>
      <span className="mtm-footer-breadcrumb">{FOOTER_BREADCRUMB}</span>
    </div>
  );
}

/**
 * Vendored kit linework (design.md V6.1 rule 1: "No circles. Replace the
 * hand-drawn concentric/contour circle motif everywhere with the kit's
 * linework assets"). This REPLACES the old `ContourMotif` (concentric
 * circles - the exact "two-circle motif" the kit's own DESIGN_SYSTEM.md says
 * to remove). It is background texture only: every caller layers it behind
 * a hero image (device/people art has transparent margins the linework
 * shows through, matching the deck's aesthetic of lines flowing behind the
 * device), never used as standalone decoration or as a stand-in for a
 * missing image - see `assets.ts`'s `resolveHeroImage`, which always
 * returns a concrete image src so a hero zone is never "just linework."
 * One zone per page (the hero container already clips it), which satisfies
 * the kit's "max two linework zones per page" with room to spare.
 */
export function Linework({
  variant = 'dark',
  className,
}: {
  /** Dark pages use 18-22% opacity; light pages 10-14% (design.md V6.1 rule 1) - set via CSS per `.mtm-linework-dark`/`.mtm-linework-light`. */
  variant?: 'dark' | 'light';
  className?: string;
}): ReactElement {
  return (
    <img
      className={`mtm-linework mtm-linework-${variant}${className ? ` ${className}` : ''}`}
      src={variant === 'dark' ? LINEWORK_DARK : LINEWORK_LIGHT}
      alt=""
      aria-hidden="true"
    />
  );
}
